<template>
  <div class="tooth-selector">
    <div class="selector-title">
      <h3>Tooth Selector</h3>
      <!-- <div class="selection-mode">
        <label class="mode-switcher">
          <span class="mode-label" :class="{ active: !singleSelectionMode }">Multiple</span>
          <div class="toggle-switch" @click="toggleSelectionMode">
            <div class="toggle-slider" :class="{ 'single-mode': singleSelectionMode }"></div>
          </div>
          <span class="mode-label" :class="{ active: singleSelectionMode }">Single</span>
        </label>
      </div> -->
      <button type="button" class="close-button" @click="$emit('close')" v-if="showCloseButton">
        <svg width="20" height="20" viewBox="0 0 24 24">
          <path fill="currentColor"
            d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
        </svg>
      </button>
    </div>

    <div class="tooth-grid">
      <!-- Upper Jaw - one row with spacer in middle -->
      <div class="tooth-row">
        <div v-for="toothNumber in upperLeftTeeth" :key="'ul-' + toothNumber" class="tooth"
          :class="{ selected: selectedTeeth.includes(toothNumber.toString()) }" @click="toggleTooth(toothNumber.toString())">
          {{ toothNumber }}
        </div>

        <!-- Midline spacer -->
        <div class="tooth-spacer"></div>

        <div v-for="toothNumber in upperRightTeeth" :key="'ur-' + toothNumber" class="tooth"
          :class="{ selected: selectedTeeth.includes(toothNumber.toString()) }" @click="toggleTooth(toothNumber.toString())">
          {{ toothNumber }}
        </div>
      </div>

      <!-- Divider -->
      <div class="jaw-divider"></div>

      <!-- Lower Jaw - one row with spacer in middle -->
      <div class="tooth-row">
        <div v-for="toothNumber in lowerLeftTeeth" :key="'ll-' + toothNumber" class="tooth"
          :class="{ selected: selectedTeeth.includes(toothNumber.toString()) }" @click="toggleTooth(toothNumber.toString())">
          {{ toothNumber }}
        </div>

        <!-- Midline spacer -->
        <div class="tooth-spacer"></div>

        <div v-for="toothNumber in lowerRightTeeth" :key="'lr-' + toothNumber" class="tooth"
          :class="{ selected: selectedTeeth.includes(toothNumber.toString()) }" @click="toggleTooth(toothNumber.toString())">
          {{ toothNumber }}
        </div>
      </div>
    </div>

    <div class="selection-summary" v-if="selectedTeeth.length > 0">
      <span>Selected: <strong>{{ selectedTeeth.join(', ') }}</strong></span>
    </div>

    <div class="action-buttons" v-if="showActionButtons">
      <button type="button" class="btn-secondary" @click="$emit('close')">
        Cancel
      </button>
      <button type="button" class="btn-primary" @click="confirmSelection" :disabled="selectedTeeth.length === 0">
        Confirm Selection
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ToothSelector',

  props: {
    value: {
      type: String,
      default: ''
    },
    showCloseButton: {
      type: Boolean,
      default: true
    },
    showActionButtons: {
      type: Boolean,
      default: true
    },
    singleSelect: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedTeeth: this.value ? this.value.split(',').map(t => t.trim()) : [],
      singleSelectionMode: this.singleSelect,

      // FDI Notation
      upperLeftTeeth: [18, 17, 16, 15, 14, 13, 12, 11],
      upperRightTeeth: [21, 22, 23, 24, 25, 26, 27, 28],
      lowerLeftTeeth: [48, 47, 46, 45, 44, 43, 42, 41],
      lowerRightTeeth: [31, 32, 33, 34, 35, 36, 37, 38]
    }
  },

  watch: {
    value(newValue) {
      this.selectedTeeth = newValue ? newValue.split(',').map(t => t.trim()) : [];
    },
    singleSelect(newValue) {
      this.singleSelectionMode = newValue;
      // If switching to single mode and we have multiple selections, keep only the first one
      if (newValue && this.selectedTeeth.length > 1) {
        this.selectedTeeth = [this.selectedTeeth[0]];
        this.$emit('input', this.selectedTeeth.join(', '));
      }
    }
  },

  methods: {
    toggleSelectionMode() {
      this.singleSelectionMode = !this.singleSelectionMode;
      
      // If switching to single mode and we have multiple selections, keep only the first one
      if (this.singleSelectionMode && this.selectedTeeth.length > 1) {
        this.selectedTeeth = [this.selectedTeeth[0]];
        if (!this.showActionButtons) {
          this.$emit('input', this.selectedTeeth.join(', '));
        }
      }
    },
    
    toggleTooth(toothNumber) {
      if (this.singleSelectionMode) {
        // In single mode, clear any existing selection and add the new one
        const isAlreadySelected = this.selectedTeeth.includes(toothNumber);
        this.selectedTeeth = isAlreadySelected ? [] : [toothNumber];
      } else {
        // In multiple mode, toggle the selection
        const index = this.selectedTeeth.indexOf(toothNumber);
        if (index > -1) {
          this.selectedTeeth.splice(index, 1);
        } else {
          this.selectedTeeth.push(toothNumber);
        }
      }

      if (!this.showActionButtons) {
        this.$emit('input', this.selectedTeeth.join(', '));
      }
    },
    
    confirmSelection() {
      this.$emit('input', this.selectedTeeth.join(', '));
      this.$emit('close');
    }
  }
}
</script>

<style scoped>
.tooth-selector {
  background: white;
  border-radius: 10px;
  border: 1px solid #E5E7EB;
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}

.selector-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.selector-title h3 {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.selection-mode {
  display: flex;
  align-items: center;
}

.mode-switcher {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.mode-label {
  font-size: 14px;
  color: #6B7280;
  transition: color 0.2s ease;
}

.mode-label.active {
  color: #111827;
  font-weight: 600;
}

.toggle-switch {
  position: relative;
  width: 40px;
  height: 20px;
  background-color: #E5E7EB;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.3s ease, background-color 0.3s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.toggle-slider.single-mode {
  transform: translateX(20px);
  background-color: #013B82;
}

.close-button {
  background: none;
  border: none;
  color: #6B7280;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-button:hover {
  background-color: #F3F4F6;
  color: #374151;
}

.tooth-grid {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.jaw {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tooth-row {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.tooth {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: 2px solid #D1D5DB;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  color: #4B5563;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #F9FAFB;
}

.tooth:hover {
  border-color: #013B82;
  background-color: #EFF6FF;
}

.tooth.selected {
  border-color: #013B82;
  background-color: #013B82;
  color: white;
}

.jaw-divider {
  height: 2px;
  background-color: #E5E7EB;
  margin: 10px 0;
}

.selection-summary {
  margin-top: 16px;
  padding: 8px 12px;
  background-color: #F3F4F6;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.btn-primary {
  background: linear-gradient(135deg, #013B82, #0A2D5C);
  color: white;
  border: none;
  box-shadow: 0 2px 4px rgba(1, 59, 130, 0.2);
}

.btn-primary:hover {
  box-shadow: 0 4px 6px rgba(1, 59, 130, 0.3);
}

.btn-primary:disabled {
  background: #9CA3AF;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-secondary {
  background: white;
  color: #4B5563;
  border: 1px solid #D1D5DB;
}

.btn-secondary:hover {
  background: #F9FAFB;
  color: #111827;
  border-color: #9CA3AF;
}

@media (max-width: 640px) {
  .tooth-grid {
    gap: 10px;
  }

  .tooth-row {
    flex-wrap: wrap;
    justify-content: center;
  }

  .tooth-spacer {
    display: none;
  }

  .tooth {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }
  
  .selector-title {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .selection-mode {
    width: 100%;
    justify-content: space-between;
  }
}

.tooth-spacer {
  width: 24px;
}
</style>